<template>
	<component
		:is="iconComponent"
		v-if="iconComponent"
		:class="['inline-block', sizeClass, className]"
		:aria-label="ariaLabel"
		:aria-hidden="!ariaLabel"
		role="img"
	/>
	<span v-else class="inline-block" :class="[sizeClass, className]" :aria-label="ariaLabel">
		<!-- Fallback: question mark for unknown icons -->
		?
	</span>
</template>

<script setup lang="ts">
import { computed, type Component } from 'vue';

// ============================================================================
// STATIC ICON IMPORTS - unplugin-icons requires static imports at build time
// Add new icons here when needed. Format: "collection:icon-name"
// ============================================================================

// Heroicons Outline
import HeroiconsArrowRight from '~icons/heroicons-outline/arrow-right';
import HeroiconsCheck from '~icons/heroicons-outline/check';
import HeroiconsLockClosed from '~icons/heroicons-outline/lock-closed';
import HeroiconsXMark from '~icons/heroicons-outline/x-mark';
import HeroiconsCreditCard from '~icons/heroicons-outline/credit-card';

// Lucide Icons
import LucideAlertTriangle from '~icons/lucide/alert-triangle';
import LucideArrowRight from '~icons/lucide/arrow-right';
import LucideAward from '~icons/lucide/award';
import LucideBarChart2 from '~icons/lucide/bar-chart-2';
import LucideBookmark from '~icons/lucide/bookmark';
import LucideBookOpen from '~icons/lucide/book-open';
import LucideBrain from '~icons/lucide/brain';
import LucideCheckCircle from '~icons/lucide/check-circle';
import LucideClipboardList from '~icons/lucide/clipboard-list';
import LucideCircleArrowRight from '~icons/lucide/circle-arrow-right';
import LucideClock from '~icons/lucide/clock';
import LucideCoffee from '~icons/lucide/coffee';
import LucideCompass from '~icons/lucide/compass';
import LucideDownload from '~icons/lucide/download';
import LucideEar from '~icons/lucide/ear';
import LucideEye from '~icons/lucide/eye';
import LucideFileText from '~icons/lucide/file-text';
import LucideFrown from '~icons/lucide/frown';
import LucideGift from '~icons/lucide/gift';
import LucideGraduationCap from '~icons/lucide/graduation-cap';
import LucideHand from '~icons/lucide/hand';
import LucideHeadphones from '~icons/lucide/headphones';
import LucideHeart from '~icons/lucide/heart';
import LucideHelpCircle from '~icons/lucide/help-circle';
import LucideHome from '~icons/lucide/home';
import LucideLightbulb from '~icons/lucide/lightbulb';
import LucideLock from '~icons/lucide/lock';
import LucideMail from '~icons/lucide/mail';
import LucideMountain from '~icons/lucide/mountain';
import LucidePalette from '~icons/lucide/palette';
import LucidePencil from '~icons/lucide/pencil';
import LucideRefreshCw from '~icons/lucide/refresh-cw';
import LucideRocket from '~icons/lucide/rocket';
import LucideSearch from '~icons/lucide/search';
import LucideShield from '~icons/lucide/shield';
import LucideSmile from '~icons/lucide/smile';
import LucideStar from '~icons/lucide/star';
import LucideTarget from '~icons/lucide/target';
import LucideTimer from '~icons/lucide/timer';
import LucideTrendingUp from '~icons/lucide/trending-up';
import LucideTrophy from '~icons/lucide/trophy';
import LucideUsers from '~icons/lucide/users';
import LucideX from '~icons/lucide/x';
import LucideZap from '~icons/lucide/zap';

// Logos (brand icons)
import LogosVisa from '~icons/logos/visa';
import LogosMastercard from '~icons/logos/mastercard';
import LogosPaypal from '~icons/logos/paypal';

// ============================================================================
// ICON REGISTRY - Maps "collection:icon-name" to imported components
// ============================================================================
const iconRegistry: Record<string, Component> = {
	// Heroicons Outline
	'heroicons-outline:arrow-right': HeroiconsArrowRight,
	'heroicons-outline:check': HeroiconsCheck,
	'heroicons-outline:lock-closed': HeroiconsLockClosed,
	'heroicons-outline:x-mark': HeroiconsXMark,
	'heroicons-outline:credit-card': HeroiconsCreditCard,

	// Lucide Icons
	'lucide:alert-triangle': LucideAlertTriangle,
	'lucide:arrow-right': LucideArrowRight,
	'lucide:award': LucideAward,
	'lucide:bar-chart-2': LucideBarChart2,
	'lucide:bookmark': LucideBookmark,
	'lucide:book-open': LucideBookOpen,
	'lucide:brain': LucideBrain,
	'lucide:check-circle': LucideCheckCircle,
	'lucide:circle-arrow-right': LucideCircleArrowRight,
	'lucide:clipboard-list': LucideClipboardList,
	'lucide:clock': LucideClock,
	'lucide:coffee': LucideCoffee,
	'lucide:compass': LucideCompass,
	'lucide:download': LucideDownload,
	'lucide:ear': LucideEar,
	'lucide:eye': LucideEye,
	'lucide:file-text': LucideFileText,
	'lucide:frown': LucideFrown,
	'lucide:gift': LucideGift,
	'lucide:graduation-cap': LucideGraduationCap,
	'lucide:hand': LucideHand,
	'lucide:headphones': LucideHeadphones,
	'lucide:heart': LucideHeart,
	'lucide:help-circle': LucideHelpCircle,
	'lucide:home': LucideHome,
	'lucide:lightbulb': LucideLightbulb,
	'lucide:lock': LucideLock,
	'lucide:mail': LucideMail,
	'lucide:mountain': LucideMountain,
	'lucide:palette': LucidePalette,
	'lucide:pencil': LucidePencil,
	'lucide:refresh-cw': LucideRefreshCw,
	'lucide:rocket': LucideRocket,
	'lucide:search': LucideSearch,
	'lucide:shield': LucideShield,
	'lucide:smile': LucideSmile,
	'lucide:star': LucideStar,
	'lucide:target': LucideTarget,
	'lucide:timer': LucideTimer,
	'lucide:trending-up': LucideTrendingUp,
	'lucide:trophy': LucideTrophy,
	'lucide:users': LucideUsers,
	'lucide:x': LucideX,
	'lucide:zap': LucideZap,

	// Logos (brand icons)
	'logos:visa': LogosVisa,
	'logos:mastercard': LogosMastercard,
	'logos:paypal': LogosPaypal,
};

// ============================================================================
// COMPONENT PROPS
// ============================================================================
interface Props {
	/** Icon name in format "collection:icon-name" */
	name: string;
	/** Predefined size classes */
	size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';
	/** Additional CSS classes */
	className?: string;
	/** Accessibility label (if not provided, icon is decorative) */
	ariaLabel?: string;
}

const props = withDefaults(defineProps<Props>(), {
	size: 'md',
	className: '',
	ariaLabel: '',
});

// Size mapping to Tailwind classes
const sizeClass = computed(() => {
	const sizes: Record<string, string> = {
		xs: 'w-3 h-3',
		sm: 'w-4 h-4',
		md: 'w-5 h-5',
		lg: 'w-6 h-6',
		xl: 'w-8 h-8',
		'2xl': 'w-10 h-10',
	};
	return sizes[props.size] || sizes.md;
});

// Get icon component from registry (synchronous - no dynamic imports)
const iconComponent = computed<Component | null>(() => {
	if (!props.name) return null;

	const component = iconRegistry[props.name];
	if (!component) {
		console.warn(
			`AppIcon: Unknown icon "${props.name}". Add it to the icon registry in AppIcon.vue.`,
		);
		return null;
	}
	return component;
});
</script>
